/**
 * Form handling utilities
 */

/**
 * Handle contact forms
 * Returns cleanup function to remove event listeners
 */
export function handleContactForms () {
  const forms = document.querySelectorAll('.contact-form, .booking-form, .ths-form')

  forms.forEach(form => {
    form.addEventListener('submit', handleFormSubmission)
  })

  // Return cleanup function
  return {
    destroy () {
      forms.forEach(form => {
        form.removeEventListener('submit', handleFormSubmission)
      })
    }
  }
}

/**
 * Handle form submission
 */
function handleFormSubmission (e) {
  e.preventDefault()

  const form = e.target
  const formData = new FormData(form)
  const submitButton = form.querySelector('[type="submit"]')

  // Disable submit button
  if (submitButton) {
    submitButton.dataset.originalText = submitButton.textContent.trim()
    submitButton.disabled = true
    submitButton.textContent = 'Submitting...'
  }

  // Basic validation
  if (!validateForm(form)) {
    resetSubmitButton(submitButton)
    return
  }

  // Submit form via AJAX
  submitForm(form, formData)
    .then(response => {
      if (response && response.success === false) {
        const errorMsg = typeof response.data === 'string'
          ? response.data
          : 'There was an error submitting your form. Please try again.'
        showErrorMessage(form, errorMsg)
        return
      }
      const redirectUrl = response && response.data && response.data.redirect_url
      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }
      showSuccessMessage(form)
      form.reset()
    })
    .catch(error => {
      showErrorMessage(form, error.message)
    })
    .finally(() => {
      resetSubmitButton(submitButton)
    })
}

/**
 * Validate form
 */
function validateForm (form) {
  const requiredFields = form.querySelectorAll('[required]')
  let isValid = true

  requiredFields.forEach(field => {
    // Checkbox validation
    if (field.type === 'checkbox') {
      if (!field.checked) {
        showFieldError(field, 'This field is required')
        isValid = false
      } else {
        clearFieldError(field)
      }
      return
    }

    // Empty field validation
    if (!field.value.trim()) {
      showFieldError(field, 'This field is required')
      isValid = false
      return
    }

    // Email format validation
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
      showFieldError(field, 'Please enter a valid email address')
      isValid = false
      return
    }

    clearFieldError(field)
  })

  return isValid
}

/**
 * Submit form via AJAX
 */
async function submitForm (form, formData) {
  // Use getAttribute to avoid form.action being shadowed by a child input[name="action"]
  const actionUrl = form.getAttribute('action') || window.location.href
  const response = await fetch(actionUrl, {
    method: 'POST',
    body: formData,
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  })

  // Always try to parse the JSON body first so we can surface WordPress
  // error messages even when the HTTP status is not 2xx.
  let data
  try {
    data = await response.json()
  } catch {
    // Response was not JSON (PHP fatal, redirect page, etc.)
    throw new Error(`Something went wrong (${response.status}). Please try again or contact us directly.`)
  }

  return data
}

/**
 * Show success message
 */
function showSuccessMessage (form) {
  const message = document.createElement('div')
  message.className = 'form-message form-message--success'
  message.textContent = 'Thank you! Your message has been sent.'

  form.parentNode.insertBefore(message, form.nextSibling)

  setTimeout(() => {
    message.remove()
  }, 5000)
}

/**
 * Show error message
 */
function showErrorMessage (form, errorText) {
  const message = document.createElement('div')
  message.className = 'form-message form-message--error'
  message.textContent = errorText || 'There was an error submitting your form. Please try again.'

  form.parentNode.insertBefore(message, form.nextSibling)

  setTimeout(() => {
    message.remove()
  }, 5000)
}

/**
 * Show field error
 */
function showFieldError (field, message) {
  clearFieldError(field)

  field.classList.add('has-error')
  const error = document.createElement('span')
  error.className = 'field-error'
  error.textContent = message

  field.parentNode.appendChild(error)
}

/**
 * Clear field error
 */
function clearFieldError (field) {
  field.classList.remove('has-error')
  const existingError = field.parentNode.querySelector('.field-error')
  if (existingError) {
    existingError.remove()
  }
}

/**
 * Reset submit button
 */
function resetSubmitButton (button) {
  if (button) {
    button.disabled = false
    button.textContent = button.dataset.originalText || 'Submit'
  }
}
